import { Card, Colors, Heading, Text } from '@amelio/ui-library';

import { COMPENSATION_BUDGET_LAYOUT, COMPENSATION_SECTION_TITLE } from '../compensationStyles';
import { BudgetCard } from './BudgetCard';
import { StrategyCard } from './StrategyCard';
import { isMissingAudienceCriterion } from '../utils/audienceRequest';

import type { CompensationDraftAction, CompensationDraftState } from '../utils/draftReducer';
import type { CompensationComputedView, CompensationLevel } from '@/types/compensation';
import type { Dispatch } from 'react';

import { useAmelioTranslations } from '@/hooks';

interface BudgetSectionProps {
    draft: CompensationDraftState;
    dispatch: Dispatch<CompensationDraftAction>;
    computed: CompensationComputedView | undefined;
    levels: CompensationLevel[];
    canEdit: boolean;
    /** Absent for a reader who may not record salaries. */
    onImportSalaries?: () => void;
}

/** Section 2: how much the envelope holds, and the strategy that spreads it once people are in it. */
export const BudgetSection = ({
    draft,
    dispatch,
    computed,
    levels,
    canEdit,
    onImportSalaries,
}: BudgetSectionProps) => {
    const { t } = useAmelioTranslations();

    const hasLines = (computed?.lines.length ?? 0) > 0;

    return (
        <Card
            glass={'lg'}
            borderRadius={'3xl'}
            dataTestId={'compensation-budget-section'}
        >
            <Heading
                level={2}
                color={Colors.BASE_500}
                className={`${COMPENSATION_SECTION_TITLE} mb-[16px]`}
            >
                {t('compensation.budgetCard.title')}
            </Heading>

            <div className={COMPENSATION_BUDGET_LAYOUT}>
                <BudgetCard
                    budgetAmount={draft.budgetAmount}
                    budgetInputMode={draft.budgetInputMode}
                    computed={computed}
                    canEdit={canEdit}
                    onBudgetChange={(amount) => dispatch({ type: 'setBudget', amount })}
                    onBudgetInputModeChange={(mode) =>
                        dispatch({ type: 'setBudgetInputMode', mode })
                    }
                    onImportSalaries={onImportSalaries}
                />

                {hasLines ? (
                    <StrategyCard
                        distributionMode={draft.distributionMode}
                        levelMultipliers={draft.levelMultipliers}
                        scoreThreshold={draft.scoreThreshold}
                        scoreSpread={draft.scoreSpread}
                        levels={levels}
                        computed={computed}
                        canEdit={canEdit}
                        onDistributionModeChange={(mode) =>
                            dispatch({ type: 'setDistributionMode', mode })
                        }
                        onLevelMultiplierChange={(levelId, value) =>
                            dispatch({ type: 'setLevelMultiplier', levelId, value })
                        }
                        onScoreThresholdChange={(value) =>
                            dispatch({ type: 'setScoreThreshold', value })
                        }
                        onScoreSpreadChange={(value) => dispatch({ type: 'setScoreSpread', value })}
                    />
                ) : (
                    <Text color={Colors.BASE_500}>
                        {t(
                            isMissingAudienceCriterion(draft.audience)
                                ? 'compensation.empty.noAudience.message'
                                : 'compensation.empty.noEvaluation.message',
                        )}
                    </Text>
                )}
            </div>
        </Card>
    );
};
